import React, { useEffect, useState } from "react";
import { 
    Container,
    Box,
    Typography,
    Button,
    Chip,
    Grid,
    Tabs,
    Tab,
    Stack,
    Alert
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { opportunitiesService } from "../opportunities/opportunitiesService";
import ImageGallery from "../components/ImageGallery";
import { useBatchSerialization } from "../hooks/useSerialization";

const TestOpportunities = () => {
    const navigate = useNavigate();
    const [opportunities, setOpportunities] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedTab, setSelectedTab] = useState(0);
    
    // Use serialization hook for opportunities
    const { 
        serializedEntities: serializedOpportunities, 
        loading: serializationLoading, 
        errors: serializationErrors,
        refresh: refreshSerialization 
    } = useBatchSerialization("Opportunity", opportunities);

    useEffect(() => {
        loadOpportunities();
    }, []);

    const loadOpportunities = async () => {
        try {
            setLoading(true);
            const data = await opportunitiesService.getActiveOpportunities();
            setOpportunities(data);
        } catch (error) {
            // Handle error silently
        } finally {
            setLoading(false);
        }
    };

    const handleLearnMore = (opportunityId) => {
        navigate(`/opportunity/${opportunityId}`);
    };

    const getRiskColor = (riskLevel) => {
        switch (riskLevel?.toLowerCase()) {
        case "low": return "success";
        case "medium": return "warning";
        case "high": return "error";
        default: return "default";
        }
    };

    const handleTabChange = (event, newValue) => {
        setSelectedTab(newValue);
    };

    if (loading || serializationLoading) {
        return (
            <Container maxWidth="lg" sx={{ py: 4 }}>
                <Typography>
                    {loading ? "Loading opportunities..." : "Serializing opportunities..."}
                </Typography>
            </Container>
        );
    }

    return (
        <Container maxWidth="lg" sx={{ py: 4 }}>
            <Stack spacing={4}>
                <Box textAlign="center">
                    <Typography variant="h2" color="primary" sx={{ fontWeight: "bold", mb: 2 }}>
            Test Opportunities Design
                    </Typography>
                    <Typography variant="h6" color="text.secondary">
            Testing the design with {opportunities.length} fake opportunities
                    </Typography>
                    
                    {/* Serialization Status */}
                    <Box sx={{ mt: 2 }}>
                        <Stack direction="row" spacing={2} justifyContent="center" alignItems="center">
                            <Chip 
                                label={`Serialized: ${serializedOpportunities.length}/${opportunities.length}`}
                                color={serializedOpportunities.length === opportunities.length ? "success" : "warning"}
                                size="small"
                            />
                            <Chip 
                                label={`Version: ${serializedOpportunities[0]?.version || "N/A"}`}
                                color="primary"
                                size="small"
                            />
                            <Button 
                                size="small" 
                                variant="outlined"
                                onClick={refreshSerialization}
                            >
                                Refresh Serialization
                            </Button>
                        </Stack>
                    </Box>
                    
                    {/* Serialization Errors */}
                    {Object.keys(serializationErrors).length > 0 && (
                        <Alert severity="warning" sx={{ mt: 2 }}>
                            Serialization errors detected for {Object.keys(serializationErrors).length} opportunities
                        </Alert>
                    )}
                </Box>

                <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
                    <Tabs 
                        value={selectedTab} 
                        onChange={handleTabChange} 
                        variant="scrollable"
                        scrollButtons="auto"
                    >
                        {opportunities.map((opportunity, _index) => (
                            <Tab 
                                key={opportunity.id} 
                                label={opportunity.title.split(" - ")[0]}
                                sx={{ minWidth: 120 }}
                            />
                        ))}
                    </Tabs>
                </Box>

                {serializedOpportunities.map((serializedOpportunity, index) => {
                    const opportunity = opportunities.find(o => o.id === serializedOpportunity.id);
                    return (
                        <Box key={opportunity.id} sx={{ display: selectedTab === index ? "block" : "none" }}>
                            <Stack spacing={3}>
                                <Box>
                                    <Typography variant="h3" color="primary" sx={{ fontWeight: "bold", mb: 1 }}>
                                        {opportunity.title}
                                    </Typography>
                                    <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                                        <Chip 
                                            label={`${opportunity.riskLevel} Risk`}
                                            color={getRiskColor(opportunity.riskLevel)}
                                            size="small"
                                        />
                                        <Chip 
                                            label={opportunity.propertyType}
                                            color="primary"
                                            size="small"
                                        />
                                        <Chip 
                                            label={opportunity.status}
                                            color="success"
                                            size="small"
                                        />
                                    </Stack>
                                    <Typography variant="body1" color="text.secondary">
                                        {opportunity.description}
                                    </Typography>
                                </Box>

                                <Box>
                                    <Typography variant="h5" sx={{ mb: 2 }}>
                  Property Images ({opportunity.images?.length || 0} images)
                                    </Typography>
                                    <ImageGallery 
                                        media={opportunity.media || []}
                                        images={opportunity.images || []}
                                        title={opportunity.title}
                                    />
                                </Box>

                                <Box>
                                    <Typography variant="h5" sx={{ mb: 2 }}>Investment Details</Typography>
                                    <Grid container spacing={3}>
                                        <Grid item xs={6} md={3}>
                                            <Typography variant="caption" color="text.secondary">Total Amount</Typography>
                                            <Typography variant="h6" color="primary" sx={{ fontWeight: "bold" }}>
                      €{opportunity.amount?.toLocaleString()}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={6} md={3}>
                                            <Typography variant="caption" color="text.secondary">Minimum Investment</Typography>
                                            <Typography variant="h6" color="success.main" sx={{ fontWeight: "bold" }}>
                      €{opportunity.minimumInvestment?.toLocaleString()}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={6} md={3}>
                                            <Typography variant="caption" color="text.secondary">Expected Return</Typography>
                                            <Typography variant="h6" color="secondary" sx={{ fontWeight: "bold" }}>
                                                {opportunity.expectedReturn}%
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={6} md={3}>
                                            <Typography variant="caption" color="text.secondary">Duration</Typography>
                                            <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                                                {opportunity.duration} months
                                            </Typography>
                                        </Grid>
                                    </Grid>
                                </Box>

                                <Box>
                                    <Typography variant="caption" color="text.secondary">Location</Typography>
                                    <Typography variant="body1" sx={{ fontWeight: "medium" }}>
                                        {opportunity.location}
                                    </Typography>
                                </Box>

                                {/* Serialization Information */}
                                <Box>
                                    <Typography variant="h6" sx={{ mb: 2 }}>Serialization Details</Typography>
                                    <Grid container spacing={2}>
                                        <Grid item xs={6} md={3}>
                                            <Typography variant="caption" color="text.secondary">Version</Typography>
                                            <Typography variant="body2" sx={{ fontWeight: "medium" }}>
                                                {serializedOpportunity.version}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={6} md={3}>
                                            <Typography variant="caption" color="text.secondary">Serialized At</Typography>
                                            <Typography variant="body2" sx={{ fontWeight: "medium" }}>
                                                {new Date(serializedOpportunity.serializedAt).toLocaleString()}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={6} md={3}>
                                            <Typography variant="caption" color="text.secondary">Regulatory</Typography>
                                            <Typography variant="body2" sx={{ fontWeight: "medium" }}>
                                                {serializedOpportunity.regulatoryRequirement}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={6} md={3}>
                                            <Typography variant="caption" color="text.secondary">Change Reason</Typography>
                                            <Typography variant="body2" sx={{ fontWeight: "medium" }}>
                                                {serializedOpportunity.changeReason}
                                            </Typography>
                                        </Grid>
                                    </Grid>
                                </Box>

                                <Stack direction="row" spacing={2}>
                                    <Button 
                                        variant="outlined" 
                                        color="primary"
                                        onClick={() => handleLearnMore(opportunity.id)}
                                    >
                  View Full Details
                                    </Button>
                                    <Button 
                                        variant="contained" 
                                        color="success"
                                        onClick={() => handleLearnMore(opportunity.id)}
                                    >
                  Invest Now
                                    </Button>
                                </Stack>
                            </Stack>
                        </Box>
                    );
                })}

                <Box textAlign="center" sx={{ py: 4 }}>
                    <Typography variant="h6" color="text.secondary">
            Testing {opportunities.length} opportunities with image galleries
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Each opportunity has {opportunities[0]?.images?.length || 0} property images
                    </Typography>
                </Box>
            </Stack>
        </Container>
    );
};

export default TestOpportunities; 